
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ClaimButton } from "./ClaimButton";
import { ClaimDialog } from "./ClaimDialog";
import { ClaimingSuccessNotice } from "./ClaimingSuccessNotice";

interface ClaimingSectionProps {
  nhSlug: string;
  centerName: string;
  isClaimed: boolean;
}

export function ClaimingSection({ nhSlug, centerName, isClaimed }: ClaimingSectionProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const [isClaimLoading, setIsClaimLoading] = useState(false);
  const [userHasClaim, setUserHasClaim] = useState(false);
  const [claimStatus, setClaimStatus] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    role: "",
    email: "",
    contactPhone: "",
    verificationNote: "",
  });

  // Check if current user has already claimed this nursing home
  useEffect(() => {
    const checkExistingClaim = async () => {
      if (!user || !nhSlug) return;
      
      try {
        const { data, error } = await supabase
          .from('nursing_home_claims')
          .select('status')
          .eq('user_id', user.id)
          .eq('nursing_home_id', nhSlug)
          .maybeSingle();
        
        if (error) throw error;
        
        if (data) {
          setUserHasClaim(true);
          setClaimStatus(data.status);
          console.log(`User has claim with status: ${data.status}`);
        }
      } catch (err) {
        console.error("Error checking claim status:", err);
      }
    };
    
    checkExistingClaim();
  }, [user, nhSlug]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string) => {
    setFormData({ ...formData, role: value });
  };

  const handleDialogOpenChange = (open: boolean) => {
    setClaimDialogOpen(open);
    if (!open) {
      setFormData({
        role: "",
        email: "",
        contactPhone: "",
        verificationNote: "",
      });
    }
  };

  const isEmailVerified = () => {
    if (!formData.email || !centerName) return false;
    const emailDomain = formData.email.split('@')[1]?.toLowerCase();
    const centreNameWords = centerName.toLowerCase().split(' ');
    return centreNameWords.some(word => 
      emailDomain?.includes(word.replace(/[^a-z0-9]/g, ''))
    );
  };

  const handleClaimSubmit = async () => {
    if (!user) {
      toast.info("You need to sign in first.");
      // Set flag to identify this user as a nursing home owner after authentication
      sessionStorage.setItem('justClaimedNursingHome', nhSlug);
      return;
    }
    setIsClaimLoading(true);
    try {
      // Check if user already has a claim for this nursing home
      const { data: existingClaim } = await supabase
        .from("nursing_home_claims")
        .select("id, status")
        .eq("nursing_home_id", nhSlug)
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (existingClaim) {
        toast.info(`You've already submitted a claim for this nursing home. Current status: ${existingClaim.status}`);
        setClaimDialogOpen(false);
        setUserHasClaim(true);
        setClaimStatus(existingClaim.status);
        setIsClaimLoading(false);
        return;
      }

      const emailVerified = isEmailVerified();

      const { error } = await supabase.from("nursing_home_claims").insert({
        nursing_home_id: nhSlug,
        user_id: user.id,
        status: "pending",
        verification_documents: {
          role: formData.role,
          email: formData.email,
          contactPhone: formData.contactPhone,
          note: formData.verificationNote,
          emailVerified,
        },
        notes: formData.verificationNote,
      });

      if (error) throw error;

      setClaimDialogOpen(false);
      setClaimSubmitted(true);
      setUserHasClaim(true);
      setClaimStatus("pending");
      
      // Set flag to identify this user as a nursing home owner
      sessionStorage.setItem('justClaimedNursingHome', nhSlug);
      
      if (emailVerified) {
        toast.success("Your claim request has been submitted and will be reviewed shortly.");
      } else {
        toast.success("Thanks! Our team will contact you to verify your association with the facility.");
      }
    } catch (err) {
      toast.error("Failed to submit claim request.");
      console.error(err);
    } finally {
      setIsClaimLoading(false);
    }
  };

  // If nursing home is claimed (by any user)
  if (isClaimed) {
    // If claimed by current user (with approved status)
    if (userHasClaim && claimStatus === 'approved') {
      return <ClaimingSuccessNotice centerName={centerName} nhSlug={nhSlug} isOwner={true} />;
    }
    
    // Show verified badge for all other visitors
    return <ClaimingSuccessNotice centerName={centerName} nhSlug={nhSlug} isOwner={false} />;
  }

  // If user has a pending claim (canonical or legacy DB values)
  if (userHasClaim && claimStatus && claimStatus.startsWith("pending")) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-blue-800">
        <div className="font-semibold mb-1">Your claim is being processed</div>
        <div className="text-sm">
          We've received your request to claim this nursing home. Our team will review it and get back to you soon.
        </div>
      </div>
    );
  }

  if (claimSubmitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-green-800">
        <div className="font-semibold mb-1">Thanks for submitting your claim!</div>
        <div className="text-sm">
          Our team will review your request and reach out to you soon regarding the verification.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <ClaimButton onClick={() => setClaimDialogOpen(true)} />
      <ClaimDialog
        centerName={centerName}
        open={claimDialogOpen}
        onOpenChange={handleDialogOpenChange}
        isSubmitting={isClaimLoading}
        formData={formData}
        onInputChange={handleInputChange}
        onSelectChange={handleSelectChange}
        onSubmit={handleClaimSubmit}
        onShowRegistration={() => navigate(`/claim/${nhSlug}`)}
      />
    </div>
  );
}
